import { api, resolvePagination } from './api';
import { fetchClinicalNote } from './clinicalNotes';
import { getUsers } from './users';

export const fetchAppointments = async ({
    patient,
    doctor,
    statuses,
    timestamp,
    endTimestamp,
    signal,
    order = 'DESC',
    pageLimit = 50,
    pageToken,
    allPages = false
}) => {
    const appointments = [];
    let token = pageToken;
    do {
        const headers = {
            ...resolvePagination(token, pageLimit),
            'CHEART-QUERY-ORDER': order
        };
        if (patient) headers['CHEART-QUERY-PATIENT'] = patient;
        if (doctor) headers['CHEART-QUERY-DOCTOR'] = doctor;
        if (statuses)
            headers['CHEART-QUERY-APPOINTMENT-STATUS'] = JSON.stringify(statuses);
        if (timestamp) headers['CHEART-QUERY-TIMESTAMP'] = timestamp;

        const response = await api.get('/cheart/appointments', {
            headers,
            ...(signal ? { signal } : {})
        });
        const beforeEnd = (appointment) =>
            !endTimestamp ||
            new Date(appointment.ts).getTime() < new Date(endTimestamp).getTime();
        appointments.push(...response.data.filter(beforeEnd));
        const nextToken = response.headers['pagination-token'];
        const count = Number(response.headers['pagination-count']);
        // The backend returns a token even on the last page.
        if (
            !allPages ||
            (order === 'ASC' &&
                response.data.some((appointment) => !beforeEnd(appointment))) ||
            !response.data.length ||
            appointments.length >= count ||
            !nextToken ||
            nextToken === token
        )
            break;
        token = nextToken;
    } while (allPages && token);
    return appointments;
};

export const getAppointmentClinicians = async (clinicUuid) => {
    const clinicians = [];
    let token;
    do {
        const response = await getUsers(
            ['DOCTOR', 'CLINIC_CARE_GIVER'],
            50,
            token,
            undefined,
            clinicUuid
        );
        clinicians.push(...response.data);
        const nextToken = response.headers['pagination-token'];
        if (
            !response.data.length ||
            clinicians.length >= Number(response.headers['pagination-count']) ||
            !nextToken ||
            nextToken === token
        )
            break;
        token = nextToken;
    } while (token);
    return clinicians;
};

export const updateAppointmentStatus = async ({ appointment, status, ti, tf }) => {
    await api.patch('/cheart/appointments/admin', {
        appointment,
        status,
        ...(status === 'ONGOING' ? { ti } : {}),
        ...(status === 'COMPLETED' ? { tf } : {})
    });
};

export const scheduleAppointment = async ({ patient, doctor, ts, slot_duration }) => {
    const { data } = await api.post('/cheart/appointments/admin', {
        patient,
        doctor,
        ts,
        slot_duration,
        status: 'SCHEDULED'
    });
    return data;
};

export const fetchAppointmentByUuid = async (uuid) => {
    const { data } = await api.post('/cheart/appointments/fetch', {
        appointment: uuid
    });
    if (!data.note) return data;
    const note = await fetchClinicalNote(data.note);
    return { ...data, notes: note.content };
};

export const addAppointmentNotes = async ({ uuid, patient, notes }) => {
    const { data } = await api.post('/cheart/notes', {
        appointment: uuid,
        patient,
        ts: new Date().toISOString(),
        format: 'PLAIN',
        content: notes,
        priviledged: false
    });
    return data;
};

export const completeAppointment = async (uuid) => {
    await api.patch('/cheart/appointments/admin', {
        appointment: uuid,
        status: 'COMPLETED',
        tf: new Date().toISOString()
    });
};
